// Notification Service
// Centralized service for showing user notifications, alerts, and feedback
// Provides consistent UI feedback across the application

export interface NotificationOptions {
    type: 'success' | 'error' | 'warning' | 'info';
    title?: string;
    message: string;
    duration?: number; // Auto-dismiss after this many milliseconds (0 = no auto-dismiss)
    persistent?: boolean; // If true, won't auto-dismiss
    /** Called when the notification is dismissed (X or programmatic). */
    onDismiss?: (id: string) => void;
    actions?: Array<{
        label: string;
        action: () => void;
        style?: 'primary' | 'secondary' | 'danger';
    }>;
}

export interface Notification extends NotificationOptions {
    id: string;
    timestamp: number;
    dismissed: boolean;
}

class NotificationService {
    private notifications: Notification[] = [];
    private listeners: Array<(notifications: Notification[]) => void> = [];
    private nextId = 1;

    /**
     * Shows a success notification
     */
    success(message: string, options?: Partial<NotificationOptions>): string {
        return this.show({
            type: 'success',
            message,
            duration: 3000, // Auto-dismiss after 5 seconds
            ...options
        });
    }

    /**
     * Shows an error notification
     */
    error(message: string, options?: Partial<NotificationOptions>): string {
        return this.show({
            type: 'error',
            message,
            duration: 3000, // Auto-dismiss after 5 seconds
            persistent: false,
            ...options
        });
    }

    /**
     * Shows a warning notification
     */
    warning(message: string, options?: Partial<NotificationOptions>): string {
        return this.show({
            type: 'warning',
            message,
            duration: 3000, // Auto-dismiss after 5 seconds
            ...options
        });
    }

    /**
     * Shows an info notification
     */
    info(message: string, options?: Partial<NotificationOptions>): string {
        return this.show({
            type: 'info',
            message,
            duration: 3000, // Auto-dismiss after 5 seconds
            ...options
        });
    }

    /**
     * Shows a notification
     */
    show(options: NotificationOptions): string {
        const notification: Notification = {
            id: `notification-${this.nextId++}`,
            timestamp: Date.now(),
            dismissed: false,
            ...options
        };

        this.notifications.unshift(notification);
        this.notifyListeners();

        // Auto-dismiss if duration is set
        if (notification.duration && notification.duration > 0 && !notification.persistent) {
            setTimeout(() => {
                this.dismiss(notification.id);
            }, notification.duration);
        }

        return notification.id;
    }

    /**
     * Dismisses a notification
     */
    dismiss(id: string): void {
        const index = this.notifications.findIndex(n => n.id === id);
        if (index !== -1) {
            const n = this.notifications[index];
            n.onDismiss?.(id);
            n.dismissed = true;
            this.notifyListeners();

            // Remove from array after animation
            setTimeout(() => {
                this.notifications = this.notifications.filter(x => x.id !== id);
                this.notifyListeners();
            }, 300);
        }
    }

    /**
     * Dismisses all notifications
     */
    dismissAll(): void {
        this.notifications.forEach(notification => {
            notification.dismissed = true;
        });
        this.notifyListeners();

        setTimeout(() => {
            this.notifications = [];
            this.notifyListeners();
        }, 300);
    }

    /**
     * Gets all active notifications
     */
    getNotifications(): Notification[] {
        return this.notifications.filter(n => !n.dismissed);
    }

    /**
     * Subscribes to notification changes
     */
    subscribe(listener: (notifications: Notification[]) => void): () => void {
        this.listeners.push(listener);

        // Return unsubscribe function
        return () => {
            const index = this.listeners.indexOf(listener);
            if (index !== -1) {
                this.listeners.splice(index, 1);
            }
        };
    }

    /**
     * Notifies all listeners of changes
     */
    private notifyListeners(): void {
        this.listeners.forEach(listener => {
            listener([...this.notifications]);
        });
    }

    /**
     * Shows API error notification with proper formatting
     */
    showApiError(error: any, context?: string): string {
        let message = 'An unexpected error occurred';
        let title = 'Error';

        if (error.message) {
            message = error.message;
        }

        if (context) {
            title = `${context} Error`;
        }

        // Handle specific error types
        if (error.statusCode) {
            switch (error.statusCode) {
                case 401:
                    title = 'Authentication Required';
                    message = 'Please log in again to continue';
                    break;
                case 403:
                    title = 'Access Denied';
                    message = 'You do not have permission to perform this action';
                    break;
                case 404:
                    title = 'Not Found';
                    message = 'The requested resource was not found';
                    break;
                case 422:
                    title = 'Validation Error';
                    if (error.errors && error.errors.length > 0) {
                        message = error.errors.map((e: any) => e.message).join(', ');
                    }
                    break;
                case 429:
                    title = 'Too Many Requests';
                    message = 'Please wait a moment before trying again';
                    break;
                case 500:
                    title = 'Server Error';
                    message = 'Something went wrong on our end. Please try again later';
                    break;
            }
        }

        return this.error(message, { title });
    }

    /**
     * Shows API success notification
     */
    showApiSuccess(message: string, context?: string): string {
        const title = context ? `${context} Success` : 'Success';
        return this.success(message, { title });
    }

    /**
     * Shows loading notification
     */
    showLoading(message: string, context?: string): string {
        const title = context ? `${context} Loading` : 'Loading';
        return this.info(message, {
            title,
            persistent: true,
            duration: 0
        });
    }

    /**
     * Updates a loading notification to success
     */
    updateToSuccess(id: string, message: string): void {
        const notification = this.notifications.find(n => n.id === id);
        if (notification) {
            notification.type = 'success';
            notification.message = message;
            notification.persistent = false;
            notification.duration = 3000;
            this.notifyListeners();

            // Auto-dismiss after 3 seconds
            setTimeout(() => {
                this.dismiss(id);
            }, 3000);
        }
    }

    /**
     * Updates a loading notification to error
     */
    updateToError(id: string, message: string): void {
        const notification = this.notifications.find(n => n.id === id);
        if (notification) {
            notification.type = 'error';
            notification.message = message;
            notification.persistent = false;
            notification.duration = 3000;
            this.notifyListeners();

            // Auto-dismiss after 5 seconds
            setTimeout(() => {
                this.dismiss(id);
            }, 3000);
        }
    }
}

// Export singleton instance
export const notificationService = new NotificationService();
